import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Navbar } from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Footer } from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import { Check, ArrowRight, MapPin, Ruler, FileText, Users, ChevronRight, Zap, Shield, Clock, Star, BarChart3 } from 'lucide-react';

export default function RoofMeasurementSoftware() {
  const navigate = useNavigate();
  const { isAuthenticated, openAuthModal } = useAuth();

  const handleGetStarted = () => {
    if (isAuthenticated) {
      navigate('/dashboard');
    } else {
      openAuthModal('register');
    }
  };

  const steps = [
    {
      step: '01',
      icon: MapPin,
      title: 'Enter an Address',
      description: 'Type any US address and our satellite imagery zooms in to the property in seconds.',
    },
    {
      step: '02',
      icon: Ruler,
      title: 'Outline the Roof',
      description: 'Click to draw the roof outline on the satellite image. Our tools auto-calculate area, pitch, and perimeter.',
    },
    {
      step: '03',
      icon: FileText,
      title: 'Get Your Report',
      description: 'Instantly generate a professional PDF with total squares, material lists, and waste calculations.',
    },
  ];

  const features = [
    {
      icon: Zap,
      title: 'Instant Measurements',
      description: 'No waiting 24-48 hours. Get accurate roof measurements in seconds, not days.',
    },
    {
      icon: BarChart3,
      title: 'Material Calculator',
      description: 'Automatic material lists: shingles, underlayment, starter strip, ridge cap, nails, and drip edge.',
    },
    {
      icon: Shield,
      title: 'Pitch Adjustment',
      description: 'Select roof pitch from 0/12 to 18/12. Area and materials auto-recalculate with the correct factor.',
    },
    {
      icon: BarChart3,
      title: 'Gutter Calculator',
      description: 'Auto-calculated gutter estimates from roof perimeter: linear feet, downspouts, end caps, corners, hangers, and sealant.',
    },
    {
      icon: FileText,
      title: 'PDF Reports',
      description: 'Professional, print-ready PDF reports with your measurements, materials, and property address.',
    },
    {
      icon: Users,
      title: 'Team Features',
      description: 'Invite your crew. Contractor plan includes unlimited team members with shared measurements.',
    },
  ];

  return (
    <div className="min-h-screen bg-slate-950" data-testid="roof-measurement-software-page">
      <Helmet>
        <title>Roof Measurement Software for Contractors | SquareCount</title>
        <meta name="description" content="Professional roof measurement software for roofing contractors. Satellite-based measurements, editable material lists, job costing, and branded PDF reports — unlimited for one flat monthly price. Free 7-day Contractor trial." />
        <meta property="og:title" content="Roof Measurement Software — Instant Satellite Reports | SquareCount" />
        <meta property="og:description" content="Professional roof measurement software with satellite imagery, editable material lists, job costing, and branded PDF reports. Unlimited for one flat monthly price." />
        <link rel="canonical" href="https://squarecount.io/roof-measurement-software" />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: [
            { '@type': 'Question', name: 'What is the best roof measurement software for contractors?', acceptedAnswer: { '@type': 'Answer', text: 'For most contractors, the SquareCount Contractor plan ($79/month, everything included — unlimited measurements, editable shingle/tile/metal material lists, valley and ridge takeoffs, job costing, and branded shareable reports) is the best overall value versus per-report pricing from EagleView, Roofr, and QuickMeasure — and every new account starts with a free 7-day Contractor trial. EagleView remains the choice when certified, exhaustive detail is required for insurance claims; Hover is best for 3D models from on-site photos.' } },
            { '@type': 'Question', name: 'What is the cheapest roof measurement software?', acceptedAnswer: { '@type': 'Answer', text: 'SquareCount is among the cheapest: flat unlimited plans start at $19/month for budget shingle-only basics, and the all-in Contractor plan is $79/month, plus a free tier of 3 measurements per month and pay-as-you-go report packs from $9.99 — versus per-report fees of roughly $40-75 from EagleView or per-report/per-property pricing from Roofr, QuickMeasure, and Hover.' } },
            { '@type': 'Question', name: 'Can I measure a roof from satellite without climbing it?', acceptedAnswer: { '@type': 'Answer', text: 'Yes. SquareCount measures any U.S. roof from satellite imagery in about 60 seconds — no ladder or site visit required. Enter the address, trace the roof, and get pitch-adjusted area, squares, material lists, and a professional PDF report.' } },
          ],
        })}</script>
      </Helmet>
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1665823317247-336bf9802ee2?w=1920&q=80"
            alt="Aerial roof measurement"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-slate-900/90" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 py-20 lg:py-28">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-orange-500/20 border border-orange-500/30 rounded-full px-4 py-2 mb-6">
              <Ruler className="w-4 h-4 text-orange-400" />
              <span className="text-orange-300 text-sm font-medium">Trusted by Roofing Contractors Nationwide</span>
            </div>

            <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-7xl text-white tracking-tight leading-none mb-6">
              Roof Measurement<br />
              <span className="text-orange-500">Software</span><br />
              That Pays for Itself
            </h1>

            <p className="text-xl text-slate-300 mb-8 max-w-xl">
              Measure any roof in seconds using satellite imagery. Get instant area calculations,
              editable material lists, job costing, and professional PDF reports — <span className="text-orange-400 font-semibold">unlimited, for one flat monthly price</span>.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={handleGetStarted}
                data-testid="rms-hero-cta"
                className="bg-orange-500 hover:bg-orange-400 text-white h-14 px-8 font-heading font-bold tracking-wide text-lg btn-tactile"
              >
                Start Your 7-Day Contractor Trial
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button
                onClick={() => navigate('/pricing')}
                data-testid="rms-hero-try"
                className="h-14 px-8 font-heading font-bold tracking-wide text-lg border-2 border-white/20 text-white hover:bg-white/10 hover:border-white/40 bg-transparent"
              >
                Compare Plans &amp; Pricing
              </Button>
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-slate-400 text-sm">
              <span className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-500" /> Satellite imagery
              </span>
              <span className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-500" /> Material lists included
              </span>
              <span className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-500" /> Cancel anytime
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-slate-900/50" data-testid="rms-how-it-works">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-3">
              How It Works
            </h2>
            <p className="text-slate-400 max-w-xl mx-auto">
              Three simple steps to a professional roof measurement. No scheduling, no climbing, no waiting.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((item) => {
              const Icon = item.icon;
              return (
                <div key={item.step} className="relative text-center group">
                  <div className="text-6xl font-heading font-black text-slate-800 mb-4 group-hover:text-orange-500/20 transition-colors">
                    {item.step}
                  </div>
                  <div className="w-14 h-14 bg-orange-500 rounded-md flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="font-heading font-bold text-lg text-white mb-2">{item.title}</h3>
                  <p className="text-slate-400 text-sm">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-16 bg-slate-950">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-3">
              Everything You Need to Bid Faster
            </h2>
            <p className="text-slate-400 max-w-xl mx-auto">
              Professional-grade tools designed for roofing contractors who want accuracy without the overhead.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature) => {
              const Icon = feature.icon;
              return (
                <div
                  key={feature.title}
                  className="p-6 bg-slate-900/50 border border-slate-800 hover:border-orange-500/30 rounded-lg transition-colors group"
                >
                  <div className="w-12 h-12 bg-orange-500 rounded-md flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-heading font-bold text-lg text-white mb-2">{feature.title}</h3>
                  <p className="text-slate-400 text-sm">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section className="py-16 bg-slate-900/50" data-testid="rms-comparison">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-10">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-3">
              How SquareCount compares to other roof measurement tools
            </h2>
            <p className="text-slate-400 max-w-xl mx-auto">
              An honest look at the top tools by pricing model and best use.
            </p>
          </div>

          <div className="overflow-x-auto rounded-xl border border-slate-800 bg-slate-900/60">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-800/60">
                <tr>
                  <th className="p-4 font-heading font-bold text-slate-300">Tool</th>
                  <th className="p-4 font-heading font-bold text-slate-300">Pricing model</th>
                  <th className="p-4 font-heading font-bold text-slate-300">Best for</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['SquareCount', 'Flat $79/mo Contractor — everything included, unlimited (budget plans from $19/mo)', 'The whole takeoff: fast satellite measurements, editable material lists, valley/ridge, job costing'],
                  ['EagleView', 'Per report (~$40–75)', 'Insurance/storm-restoration claims needing certified detail'],
                  ['Hover', 'Per property', '3D models and visualizations from on-site photos'],
                  ['Roofr', 'Per report', 'Contractors wanting a measurement report + CRM'],
                  ['QuickMeasure (GAF)', 'Per report', 'Quick per-job measurement reports'],
                  ['Google Earth', 'Free', 'A rough manual outline with no roofing math'],
                ].map((t, i) => (
                  <tr key={t[0]} className={`border-t border-slate-800 ${i === 0 ? 'bg-orange-500/10' : ''}`}>
                    <td className="p-4 font-semibold text-white">
                      {t[0]}
                      {i === 0 && <span className="ml-2 text-xs text-orange-400 font-bold">Best value</span>}
                    </td>
                    <td className="p-4 text-slate-300">{t[1]}</td>
                    <td className="p-4 text-slate-400">{t[2]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-slate-500 mt-3">Pricing models are described generally and may change; check each provider for current pricing.</p>

          <div className="max-w-3xl mx-auto mt-10 space-y-4 text-slate-300">
            <h3 className="font-heading font-bold text-xl text-white">Our take: which should you choose?</h3>
            <p><strong className="text-white">Choose SquareCount</strong> if you want the whole takeoff in one flat-rate tool — the $79/month Contractor plan includes unlimited measurements and PDF reports, editable shingle, tile &amp; metal material lists, valley &amp; ridge takeoffs, job costing, shareable branded reports, and team members. (On a tight budget, shingle-only basics start at $19/month.)</p>
            <p><strong className="text-white">Choose EagleView</strong> if you need certified, exhaustive detail for insurance or storm-restoration claims and can absorb per-report pricing.</p>
            <p><strong className="text-white">Choose Hover</strong> if you specifically need 3D models and are willing to capture photos on-site.</p>
            <p><strong className="text-white">Google Earth</strong> is free, but only gives a rough outline — you do all the roofing math (pitch, waste, materials) by hand.</p>
          </div>
        </div>
      </section>

      {/* Pricing Preview */}
      <section className="py-16 bg-slate-900 noise-overlay">
        <div className="relative z-10 max-w-5xl mx-auto px-6">
          <div className="text-center mb-10">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-3">
              Simple Pricing, No Surprises
            </h2>
            <p className="text-slate-400 max-w-xl mx-auto">
              One flat rate. Unlimited measurements. No per-report fees.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            {/* Contractor Plan */}
            <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg border-2 border-orange-500 p-6 relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="bg-orange-500 text-white text-xs font-bold uppercase px-3 py-1 rounded-full">
                  Most Popular
                </span>
              </div>
              <h3 className="font-heading font-bold text-xl text-white text-center mb-2">Contractor</h3>
              <div className="text-center mb-4">
                <span className="text-4xl font-heading font-bold text-white">$79</span>
                <span className="text-slate-400">/month</span>
              </div>
              <ul className="space-y-2 mb-6">
                {['Unlimited measurements & reports', 'Shingle, tile & metal material lists', 'Valley & ridge takeoffs', 'Editable reports & job cost estimator', 'Shareable client reports + CSV', 'Branding, white-label & team members'].map((f) => (
                  <li key={f} className="flex items-center gap-2 text-sm text-slate-300">
                    <Check className="w-4 h-4 text-green-500 shrink-0" /> {f}
                  </li>
                ))}
              </ul>
              <Button
                onClick={handleGetStarted}
                data-testid="rms-contractor-cta"
                className="w-full bg-orange-500 hover:bg-orange-400 text-white h-12 font-heading font-bold tracking-wide"
              >
                Start Your 7-Day Contractor Trial
              </Button>
            </div>

            {/* Pro Plan */}
            <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg border border-slate-700 p-6">
              <h3 className="font-heading font-bold text-xl text-white text-center mb-2">Pro</h3>
              <div className="text-center mb-4">
                <span className="text-4xl font-heading font-bold text-white">$19</span>
                <span className="text-slate-400">/month</span>
              </div>
              <ul className="space-y-2 mb-6">
                {['Unlimited measurements', 'PDF reports with materials', 'Gutter estimates included', 'Pitch calculator', 'Cancel anytime'].map((f) => (
                  <li key={f} className="flex items-center gap-2 text-sm text-slate-300">
                    <Check className="w-4 h-4 text-green-500 shrink-0" /> {f}
                  </li>
                ))}
              </ul>
              <p className="text-xs text-slate-500 mb-4">Shingle-only. No report editing, job costing, or branding.</p>
              <Button
                onClick={() => navigate('/pricing')}
                data-testid="rms-pro-cta"
                className="w-full bg-slate-700 hover:bg-slate-600 text-white h-12 font-heading font-bold tracking-wide"
              >
                Compare Plans
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-orange-500">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-4">
            Start Measuring Roofs Today
          </h2>
          <p className="text-orange-100 text-lg mb-8 max-w-2xl mx-auto">
            Professional roof measurement software that saves you time and money on every job.
            Try every Contractor feature free for 7 days.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => navigate('/pricing')}
              data-testid="rms-bottom-cta"
              className="bg-slate-900 hover:bg-slate-800 text-white h-14 px-10 font-heading font-bold tracking-wide text-lg btn-tactile"
            >
              View Plans & Pricing
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
